export interface MsalAccountIdentityHints {
    /**
     * Email or username hint for suggesting the account to sign in.
     * This may vary depending on how the user authenticated (UPN, alias, or email),
     * and should NOT be used to retrieve cached accounts.
     */
    loginHint?: string;

    /**
     * Session ID for enabling silent single sign-on (SSO), if available.
     * Only present when using Office's SSO token method.
     */
    sid?: string;

    /**
     * Unique user identifier for MSAL cache lookup.
     * Format: "<userObjectId>.<tenantId>".
     */
    homeAccountId?: string;
}

export class OfficeIdentityContextResolver {
    /**
     * Attempt to use the best available OfficeJS methods and properties to fetch user identity Info.
     * getAuthContext → getAccessToken → mailbox email
     *
     * @returns A promise that resolves with the identity hints for the current user for use with MSAL.
     * @throws If a msal instance can't be created.
     */
    public async resolve(): Promise<MsalAccountIdentityHints> {
        console.info('Starting identity resolution process...');

        // 1. Try Office.auth.getAuthContext (preferred modern Office API)
        if (Office.context.requirements.isSetSupported('NestedAppAuth', '1.1') && Office.auth?.getAuthContext) {
            console.debug('NestedAppAuth is supported. Attempting Office.auth.getAuthContext...');
            try {
                const authContext = await Office.auth.getAuthContext();

                if (authContext) {
                    const result: MsalAccountIdentityHints = {
                        loginHint: authContext.loginHint || undefined,
                        sid: undefined,
                        homeAccountId: `${authContext.userObjectId}.${authContext.tenantId}`,
                    };
                    console.info('Identity resolved via getAuthContext:', result);
                    return result;
                }
            } catch (error) {
                console.warn('Office.auth.getAuthContext failed:', error);
            }
        } else {
            console.debug('NestedAppAuth not supported or Office.auth.getAuthContext unavailable. Skipping...');
        }

        // 2. Try Office.auth.getAccessToken (SingleSignOn legacy fallback)
        if (Office.context.requirements.isSetSupported('IdentityAPI', '1.3') && Office.auth?.getAccessToken) {
            console.debug('IdentityAPI is supported. Attempting Office.auth.getAccessToken...');
            try {
                const token = await Office.auth.getAccessToken({ allowSignInPrompt: true });
                const payload = this.decodeJwtPayload(token);
                const result: MsalAccountIdentityHints = {
                    loginHint: payload.upn || payload.preferred_username || payload.email || undefined,
                    sid: payload.sid || undefined,
                    homeAccountId: payload.oid && payload.tid ? `${payload.oid}.${payload.tid}` : undefined,
                };

                console.info('Identity resolved via getAccessToken:', result);
                return result;
            } catch (error) {
                console.warn('Office.auth.getAccessToken failed or token parsing failed:', error);
            }
        } else {
            console.debug('IdentityAPI not supported or Office.auth.getAccessToken unavailable. Skipping...');
        }

        // 3. Fallback to mailbox profile email, will only work in Outlook
        const email = Office.context?.mailbox?.userProfile?.emailAddress;
        if (email) {
            console.debug('Falling back to mailbox userProfile.emailAddress...');
            const result: MsalAccountIdentityHints = {
                loginHint: email,
                sid: undefined,
                homeAccountId: undefined,
            };

            console.info('Identity resolved via mailbox profile:', result);
            return result;
        }

        // Final fallback
        console.warn('Failed to resolve identity from any source. Returning empty identity hints.');
        return {
            loginHint: undefined,
            sid: undefined,
            homeAccountId: undefined,
        } as MsalAccountIdentityHints;
    }

    private decodeJwtPayload(token: string): JwtPayload {
        const payload = token.split('.')[1];
        try {
            return JSON.parse(atob(payload));
        } catch (error) {
            console.warn('Failed to decode JWT payload:', error);
            throw error;
        }
    }
}

interface JwtPayload {
    upn?: string;
    preferred_username?: string;
    email?: string;
    sid?: string;
    oid?: string;
    tid?: string;
    [key: string]: unknown;
}
